import clsx from 'clsx';
import { Accept, useDropzone } from 'react-dropzone';
import React, { useCallback, useRef, KeyboardEvent } from 'react';
import DownloadIcon from '../../assets/icons/upload-cloud-line.svg?react';
import CancelIcon from '../../assets/icons/sidebar-cross-icon.svg?react';

interface FileInputProps {
  otherClasses?: {
    container?: string;
    label?: string;
  };
  label: string;
  selectedFiles?: File[] | null;
  onChange: (files: File[] | null) => void;
  accept?: Accept;
  name: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  errorMessage?: string[] | string;
  disabled?: boolean;
  id?: string;
  updateAccept?: boolean;
  multiple?: boolean;
  customPlaceholder?: {
    label1?: string;
    label2?: string;
    label3?: string;
  };
}

const FileInput: React.FC<FileInputProps> = ({
  otherClasses,
  label,
  onChange,
  accept,
  name,
  onBlur,
  errorMessage,
  disabled,
  selectedFiles,
  id,
  updateAccept,
  multiple = true,
  customPlaceholder,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        if (selectedFiles?.length) {
          onChange([...selectedFiles, ...acceptedFiles]);
        } else {
          onChange(acceptedFiles);
        }
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onChange, selectedFiles],
  );
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
    accept,
    multiple,
  });

  const fileInputClasses = clsx(
    otherClasses?.container,
    'flex flex-col items-start gap-2.5 cursor-pointer',
  );

  const labelClasses = clsx(
    otherClasses?.label,
    'text-form-label  font-bold -tracking-form-label font-inter-variable',
    disabled
      ? 'text-neutral-tones-text/35 cursor-not-allowed'
      : 'text-neutral-tones-text',
  );

  const handleUploadMoreClick = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    const updatedFiles = selectedFiles?.filter(
      (file) => file?.name !== fileToRemove?.name,
    );
    onChange(updatedFiles && updatedFiles.length > 0 ? updatedFiles : []);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLLabelElement>) => {
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleUploadMoreClick();
    }
  };
  return (
    <label
      {...getRootProps()}
      className={fileInputClasses}
      data-testid="file-input"
      onKeyDown={handleKeyDown}
    >
      <p
        onClick={(e) => {
          if (selectedFiles && selectedFiles?.length > 0) {
            e.preventDefault();
          }
        }}
        className={labelClasses}
      >
        {label}
      </p>
      <div
        onClick={(e) => {
          if (selectedFiles && selectedFiles?.length > 0) {
            e.preventDefault();
          }
        }}
        className={clsx(
          'rounded-10 ring-neutral-tones-text/50 bg-translucent-background flex w-full flex-1 px-5 py-15 ring-1',
        )}
      >
        {selectedFiles && selectedFiles?.length > 0 && multiple ? (
          <div
            onClick={(e) => {
              e.preventDefault();
            }}
            className="flex w-full flex-col"
          >
            <div className="flex w-full flex-col gap-2.5">
              {selectedFiles?.map((node, index) => {
                const fileExtension = node.name.split('.').pop()?.toLowerCase();
                const isVideo = node.type?.startsWith('video/');
                const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
                const isFileTooLarge = node.size > maxSize;
                return (
                  <div
                    key={index}
                    className={clsx(
                      'bg-additional-dropdown-select/10 flex w-full items-center justify-between rounded-md px-15 py-2 ring',
                      !customPlaceholder && isFileTooLarge
                        ? 'ring-brand-posh-pink ring-2'
                        : 'ring-brand-byzantine',
                    )}
                  >
                    <div className="flex w-full items-center gap-2.5">
                      {fileExtension &&
                        ['png', 'jpg', 'jpeg', 'gif'].includes(
                          fileExtension,
                        ) && (
                          <img
                            src={URL.createObjectURL(node)}
                            alt={node.name}
                            className="rounded-mdobject-fill rounded-5 h-5 w-6"
                          />
                        )}
                      <p className="font-inter-variable text-form-label text-neutral-tones-text -tracking-form-label line-clamp-1 w-full leading-4 font-bold wrap-anywhere">
                        {node?.name}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="block h-5 min-h-5 w-5 min-w-5 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveFile(node);
                      }}
                    >
                      <CancelIcon className="[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                    </button>
                  </div>
                );
              })}
            </div>
            {(customPlaceholder
              ? selectedFiles?.length < 10
              : selectedFiles?.length < 3) && (
              <>
                <hr className="border-neutral-tones-text/10 my-15 w-full" />
                <button
                  type="button"
                  className="text-brand-byzantine text-form-label -tracking-form-label flex cursor-pointer items-center gap-2.5 font-bold"
                  onClick={handleUploadMoreClick}
                >
                  <DownloadIcon className="h-6 w-6" /> Upload more
                </button>
              </>
            )}
            {errorMessage &&
              (Array.isArray(errorMessage) ? (
                <>
                  {errorMessage.map((node, index) => (
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                      key={index}
                      className="font-inter-variable text-brand-posh-pink text-form-label -tracking-form-label mt-15 font-bold wrap-anywhere italic"
                    >
                      {node}
                    </span>
                  ))}
                </>
              ) : (
                <span
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                  className="font-inter-variable text-brand-posh-pink text-form-label -tracking-form-label mt-15 font-bold wrap-anywhere italic"
                >
                  {errorMessage}
                </span>
              ))}
          </div>
        ) : (
          <div className="flex w-full flex-col items-center">
            <DownloadIcon />
            <h6
              className={clsx(
                'font-inter-variable text-neutral-tones-text -tracking-form-input mt-15 mb-1.5 text-base leading-5 font-bold',
              )}
            >
              {customPlaceholder?.label3 || 'Upload Image/Video files'}
            </h6>
            <div className="flex flex-col gap-1.5 text-center">
              {customPlaceholder ? (
                <>
                  <p className="text-form-label text-neutral-tones-text/50 -tracking-form-label leading-4 font-normal">
                    {customPlaceholder?.label1}
                  </p>
                  <p className="text-form-label text-neutral-tones-text/50 -tracking-form-label leading-4 font-normal">
                    {customPlaceholder?.label2}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-form-label text-neutral-tones-text/50 -tracking-form-label leading-4 font-normal">
                    Max 3 files allowed, Image size 5mb and video size 50mb
                    allowed.
                  </p>
                  <p className="text-form-label text-neutral-tones-text/50 -tracking-form-label leading-4 font-normal">
                    {updateAccept
                      ? `.png, .jpg/.jpeg, .mp4, .gif, .pdf, .doc, .docx`
                      : `.png, .jpg/.jpeg, .mp4, .gif`}
                  </p>
                </>
              )}
              {!multiple &&
                selectedFiles?.map((node, index) => {
                  const fileExtension = node.name
                    .split('.')
                    .pop()
                    ?.toLowerCase();
                  const isVideo = node.type?.startsWith('video/');
                  const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
                  const isFileTooLarge = node.size > maxSize;
                  return (
                    <div
                      key={index}
                      className={clsx(
                        'bg-additional-dropdown-select/10 mx-auto flex w-fit items-center justify-between rounded-md px-15 py-2 ring',
                        isFileTooLarge
                          ? 'ring-brand-posh-pink ring-2'
                          : 'ring-brand-byzantine',
                      )}
                    >
                      <div className="flex w-full items-center gap-2.5">
                        {fileExtension &&
                          ['png', 'jpg', 'jpeg', 'gif'].includes(
                            fileExtension,
                          ) && (
                            <img
                              src={URL.createObjectURL(node)}
                              alt={node.name}
                              className="rounded-mdobject-fill rounded-5 h-5 w-6"
                            />
                          )}
                        <p className="font-inter-variable text-form-label text-neutral-tones-text -tracking-form-label line-clamp-1 w-full leading-4 font-bold wrap-anywhere">
                          {node?.name}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="block h-5 min-h-5 w-5 min-w-5 cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemoveFile(node);
                        }}
                      >
                        <CancelIcon className="[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                      </button>
                    </div>
                  );
                })}
            </div>
            <span
              onClick={(e) => {
                e.preventDefault();
              }}
              className="font-inter-variable text-brand-posh-pink text-form-label -tracking-form-label mt-15 font-bold wrap-anywhere italic"
            >
              {errorMessage}
            </span>
          </div>
        )}
        <input
          id={id || name}
          name={name}
          type="file"
          disabled={disabled}
          onBlur={onBlur}
          className="absolute opacity-0"
          hidden
          {...getInputProps()}
          ref={inputRef}
        />
      </div>
    </label>
  );
};

export default FileInput;
